/** What the K-Series web view tells the network about itself (DEV-378).
 *
 * The V2 POS surface answers CORS from an allow-list rather than V1's
 * `Access-Control-Allow-Origin: *`, because a POS credential is a bearer secret living
 * in a browser and a wildcard would let any page a cashier opens spend that till's
 * authority. That trade only works if the list contains the origin this web view
 * actually sends — and nobody has read it off real hardware.
 *
 * Two answers would break the current list, and both are plausible:
 *
 *   - a SANDBOXED iframe sends `Origin: null`, which matches no entry and never can;
 *   - LSK may serve the extension from its own host rather than ours, in which case the
 *     origin is a Lightspeed domain we have not listed.
 *
 * `origin` below is the exact string the browser puts in the `Origin` header, so it is
 * the value to paste into `POS_ALLOWED_ORIGINS`. Everything else is context for why.
 */

export interface WebViewFacts {
  /** THE answer. Copy this into POS_ALLOWED_ORIGINS. */
  origin: string
  href: string
  /** `true` when the origin is opaque — an allow-list cannot ever match it. */
  opaqueOrigin: boolean
  inIframe: boolean
  /** Present when the embedder is same-origin-readable; often empty in a sandbox. */
  referrer: string
  userAgent: string
  /** Where the extension is configured to call. */
  backendBaseUrl: string
  secureContext: boolean
}

export function collectWebViewFacts(backendBaseUrl: string): WebViewFacts {
  // `location.origin` is "null" (the string) for an opaque origin, which is exactly the
  // case an allow-list cannot serve — so it is called out rather than reported as a
  // hostname that happens to look odd.
  const origin = typeof window === 'undefined' ? '' : window.location.origin

  let inIframe = false
  try {
    inIframe = window.self !== window.top
  } catch {
    // A cross-origin embedder throws on `window.top`. That throw IS the answer.
    inIframe = true
  }

  return {
    origin,
    href: typeof window === 'undefined' ? '' : window.location.href,
    opaqueOrigin: origin === 'null' || origin === '',
    inIframe,
    referrer: typeof document === 'undefined' ? '' : document.referrer,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    backendBaseUrl,
    secureContext: typeof window !== 'undefined' && window.isSecureContext,
  }
}

export interface CorsProbeResult {
  /** What actually happened, in the terms the question is asked in. */
  verdict: 'allowed' | 'blocked-by-cors' | 'network-error' | 'not-configured'
  detail: string
  /** HTTP status when the response was readable at all. */
  status?: number
}

/** Ask the POS surface a harmless question and report whether the browser let us read it.
 *
 * Deliberately an unauthenticated call to a real operation: it will be refused with 401,
 * and that is a SUCCESS for this probe. What is being tested is whether the browser
 * delivers the response at all, not whether we are authorized — a CORS failure is
 * indistinguishable from a network error in JS, so the probe reports what it can
 * distinguish rather than pretending to know more.
 *
 * A readable status of any value means the origin is on the allow-list. A TypeError with
 * no status means the browser refused before we saw anything, which for a reachable host
 * means CORS.
 */
export async function probePosCors(backendBaseUrl: string): Promise<CorsProbeResult> {
  if (!backendBaseUrl) {
    return { verdict: 'not-configured', detail: 'no POS surface configured' }
  }

  try {
    const response = await fetch(backendBaseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `include`, so the web view attaches whatever HTTP auth it holds for this
      // origin. That is the whole mechanism when the surface is same-origin: K-Series
      // authenticated the page load with Basic, the web view cached it, and this is
      // what asks for it to be replayed. Harmless cross-origin, where there is nothing
      // cached for the target to send.
      credentials: 'include',
      body: JSON.stringify({ op: 'validate', schema_version: 2, payload: {} }),
    })
    // Firebase's SPA catch-all answers HTML with 200. Reporting that as "allowed"
    // would send someone hunting for a credential problem that does not exist.
    if ((response.headers?.get?.('content-type') ?? '').includes('text/html')) {
      return {
        verdict: 'network-error',
        status: response.status,
        detail:
          `got HTML instead of JSON (HTTP ${response.status}) — the hosting rewrite for ` +
          `this path is missing, so the SPA answered instead of the API.`,
      }
    }
    return {
      verdict: 'allowed',
      status: response.status,
      detail:
        response.status === 401
          ? `reached the surface (HTTP 401), so the transport works — but no credential ` +
            `arrived. K-Series is not attaching Basic auth to this request.`
          : `reached the surface (HTTP ${response.status}) WITH a credential. This is the ` +
            `working configuration.`,
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    // `fetch` rejects identically for "CORS refused" and "host unreachable". Say so
    // rather than guessing, and let the operator tell them apart by whether the host
    // resolves at all.
    return {
      verdict: 'blocked-by-cors',
      detail:
        `the browser refused before any response was readable (${message}). ` +
        `On a reachable host this is CORS: add the origin above to POS_ALLOWED_ORIGINS.`,
    }
  }
}

/** Probe both routes at once, because the interesting answer is which one works.
 *
 * Same-origin is the one that can carry the page's Basic credentials; the absolute URL
 * is the CORS path and needs the origin on `POS_ALLOWED_ORIGINS`. Running both in one
 * tap means a single trip to the iPad settles it.
 */
export async function probeBothRoutes(
  sameOriginPath: string,
  absoluteUrl: string,
): Promise<{ sameOrigin: CorsProbeResult; absolute: CorsProbeResult }> {
  const [sameOrigin, absolute] = await Promise.all([
    probePosCors(sameOriginPath),
    probePosCors(absoluteUrl),
  ])
  return { sameOrigin, absolute }
}

/** One paste-able block, because reading values off an iPad and retyping them is how
 * transcription errors get into an allow-list. */
export function formatFacts(facts: WebViewFacts, probe?: CorsProbeResult): string {
  const lines = [
    `origin:        ${facts.origin || '(empty)'}`,
    `opaque origin: ${facts.opaqueOrigin}`,
    `in iframe:     ${facts.inIframe}`,
    `href:          ${facts.href}`,
    `referrer:      ${facts.referrer || '(none)'}`,
    `secure ctx:    ${facts.secureContext}`,
    `backend:       ${facts.backendBaseUrl || '(unset)'}`,
    `user agent:    ${facts.userAgent}`,
  ]
  if (probe) {
    lines.push(`cors probe:    ${probe.verdict}${probe.status ? ` (HTTP ${probe.status})` : ''}`)
    lines.push(`               ${probe.detail}`)
  }
  return lines.join('\n')
}
